/**
 * @file hooks/useLaboratories.ts
 * @description 實驗室字典查詢與寫入 hook（管理頁 + 採樣下拉共用）。
 *
 * 兩種查詢場景：
 *   1. 管理頁列表：用 useLaboratoriesList，活躍 + 停用都看，可搜尋、可顯示已軟刪
 *   2. 下拉選單：用 useLaboratoryOptions，pageSize=500 一次拉回，5 分鐘 staleTime
 *
 * Mutation：建立 / 更新 / 軟刪除，invalidate 全部 lab 相關 query。
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CreateLaboratoryInput, LaboratoryDto, UpdateLaboratoryInput } from '@zhcb/shared';
import { apiClient } from '@/lib/api-client';
import { fetchWithOfflineCache } from '@/pwa/offline-api-cache';

export type Laboratory = LaboratoryDto;

interface PagedResponse<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

interface SuccessEnvelope<T> {
  success: true;
  data: T;
}

interface ListOpts {
  page?: number;
  pageSize?: number;
  search?: string;
  activeOnly?: boolean;
  includeDeleted?: boolean;
  enabled?: boolean;
}

const KEY = ['settings', 'laboratories'] as const;

/** 管理頁列表查詢（含分頁、搜尋、含已刪除切換）。 */
export function useLaboratoriesList(opts: ListOpts = {}) {
  const { page = 1, pageSize = 20, search, activeOnly, includeDeleted, enabled = true } = opts;
  return useQuery({
    queryKey: [...KEY, 'list', { page, pageSize, search, activeOnly, includeDeleted }],
    queryFn: () => {
      const p = new URLSearchParams();
      p.set('page', String(page));
      p.set('pageSize', String(pageSize));
      if (search) p.set('search', search);
      if (activeOnly) p.set('activeOnly', 'true');
      if (includeDeleted) p.set('includeDeleted', 'true');
      return apiClient.get<PagedResponse<Laboratory>>(`/api/settings/laboratories?${p.toString()}`);
    },
    enabled,
  });
}

/** 下拉選項（給 SamplesPanel 等業務表單用）；一次拉 500 條啟用項，前端在 Select 內過濾。 */
export function useLaboratoryOptions(opts: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: [...KEY, 'options'],
    queryFn: async () => {
      const res = await fetchWithOfflineCache<
        PagedResponse<Laboratory> | SuccessEnvelope<PagedResponse<Laboratory>>
      >(`/api/settings/laboratories?pageSize=500&activeOnly=true`, { scope: 'field-data' });
      return normalizeLaboratoryOptionsResponse(res.data);
    },
    enabled: opts.enabled ?? true,
    // 離線時也要執行 queryFn，讓 fetchWithOfflineCache 有機會讀 IndexedDB cache。
    networkMode: 'always',
    // 字典變動頻率低，5 分鐘內走快取避免重複請求
    staleTime: 5 * 60 * 1000,
  });
}

export function normalizeLaboratoryOptionsResponse(
  raw: PagedResponse<Laboratory> | SuccessEnvelope<PagedResponse<Laboratory>>,
): PagedResponse<Laboratory> {
  if ('items' in raw) return raw;
  return raw.data;
}

export function useCreateLaboratory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateLaboratoryInput) =>
      apiClient.post<{ item: Laboratory }>('/api/settings/laboratories', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateLaboratory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: UpdateLaboratoryInput }) =>
      apiClient.patch<{ item: Laboratory }>(`/api/settings/laboratories/${id}`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeleteLaboratory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => apiClient.delete<{ ok: true }>(`/api/settings/laboratories/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
